<script>
  import {
    AlarmClock,
    ArrowLeft,
    Check,
    ClipboardList,
    FileText,
    LoaderCircle,
    Plus,
    Trash2,
    Vote,
    X
  } from 'lucide-svelte';
  import {
    conversationBoardItems,
    conversationReminders,
    createConversationBoardItemWS,
    createConversationReminderWS,
    deleteConversationBoardItemWS,
    loadConversationBoardWS,
    loadConversationRemindersWS,
    updateConversationReminderStatusWS,
    voteConversationPollWS
  } from '$lib/websocket-client.js';
  import ConversationConfirmDialog from './ConversationConfirmDialog.svelte';

  export let conversationId;

  const repeatOptions = [
    { value: 'none', label: 'Không lặp lại' },
    { value: 'daily', label: 'Hằng ngày' },
    { value: 'weekly', label: 'Hằng tuần' },
    { value: 'monthly', label: 'Hằng tháng' }
  ];

  const boardTabs = [
    { id: 'all', label: 'Tất cả' },
    { id: 'pin', label: 'Tin ghim' },
    { id: 'note', label: 'Ghi chú' },
    { id: 'poll', label: 'Bình chọn' }
  ];

  let view = 'menu';
  let boardTab = 'all';
  let loading = false;
  let saving = false;
  let error = '';
  let busyId = null;
  let lastConversationId = '';
  let lastReminderLoad = '';
  let lastBoardLoad = '';

  let showReminderModal = false;
  let reminderContent = '';
  let reminderAt = toInputDateTime(addMinutes(new Date(), 30));
  let reminderRepeat = 'none';

  let showNoteModal = false;
  let noteContent = '';
  let notePinned = false;

  let showPollModal = false;
  let pollQuestion = '';
  let pollOptions = ['', ''];
  let confirmDeleteItem = null;
  let confirmDeleteReminder = null;

  $: reminders = $conversationReminders?.conversationId === conversationId ? $conversationReminders.rows || [] : [];
  $: boardItems = $conversationBoardItems?.conversationId === conversationId ? $conversationBoardItems.rows || [] : [];
  $: filteredBoardItems = boardItems.filter((item) => {
    if (boardTab === 'pin') return item.pinned;
    if (boardTab === 'note') return item.itemType === 'note';
    if (boardTab === 'poll') return item.itemType === 'poll';
    return true;
  });
  $: canCreateReminder = reminderContent.trim().length > 0 && reminderAt && !saving;
  $: canCreateNote = noteContent.trim().length > 0 && !saving;
  $: normalizedPollOptions = pollOptions.map((option) => option.trim()).filter(Boolean);
  $: canCreatePoll = pollQuestion.trim().length > 0 && normalizedPollOptions.length >= 2 && !saving;
  $: if (conversationId !== lastConversationId) {
    lastConversationId = conversationId;
    view = 'menu';
    error = '';
    lastReminderLoad = '';
    lastBoardLoad = '';
  }
  $: if (conversationId && view === 'reminders' && lastReminderLoad !== conversationId) {
    lastReminderLoad = conversationId;
    loadReminders();
  }
  $: if (conversationId && view === 'board' && lastBoardLoad !== conversationId) {
    lastBoardLoad = conversationId;
    loadBoard();
  }

  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function addMinutes(date, minutes) {
    return new Date(date.getTime() + minutes * 60 * 1000);
  }

  function tomorrowAt(hour = 9, minute = 0) {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    date.setHours(hour, minute, 0, 0);
    return date;
  }

  function toInputDateTime(date) {
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  function formatDateTime(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Chưa có thời gian';

    return new Intl.DateTimeFormat('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date);
  }

  function formatShortDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    return new Intl.DateTimeFormat('vi-VN', {
      hour: '2-digit',
      minute: '2-digit',
      day: '2-digit',
      month: '2-digit'
    }).format(date);
  }

  async function loadReminders() {
    if (!conversationId) return;

    try {
      loading = true;
      error = '';
      await loadConversationRemindersWS(conversationId);
    } catch (err) {
      error = err?.message || 'Không thể tải nhắc hẹn.';
    } finally {
      loading = false;
    }
  }

  async function loadBoard() {
    if (!conversationId) return;

    try {
      loading = true;
      error = '';
      await loadConversationBoardWS(conversationId);
    } catch (err) {
      error = err?.message || 'Không thể tải bảng tin nhóm.';
    } finally {
      loading = false;
    }
  }

  function openReminderModal() {
    reminderContent = '';
    reminderAt = toInputDateTime(addMinutes(new Date(), 30));
    reminderRepeat = 'none';
    showReminderModal = true;
  }

  async function createReminder() {
    if (!canCreateReminder) return;

    try {
      saving = true;
      error = '';
      await createConversationReminderWS(conversationId, {
        content: reminderContent,
        remindAt: new Date(reminderAt).toISOString(),
        repeatRule: reminderRepeat
      });
      showReminderModal = false;
    } catch (err) {
      error = err?.message || 'Không thể tạo nhắc hẹn.';
    } finally {
      saving = false;
    }
  }

  async function updateReminderStatus(reminder, status) {
    if (!reminder?.id || busyId) return;

    try {
      busyId = reminder.id;
      error = '';
      await updateConversationReminderStatusWS(conversationId, reminder.id, status);
    } catch (err) {
      error = err?.message || 'Không thể cập nhật nhắc hẹn.';
    } finally {
      busyId = null;
    }
  }

  function requestDeleteReminder(reminder) {
    if (!reminder?.id || busyId) return;

    confirmDeleteReminder = reminder;
  }

  async function confirmDeleteReminderItem() {
    const reminder = confirmDeleteReminder;
    if (!reminder?.id || busyId) return;

    try {
      busyId = reminder.id;
      error = '';
      await updateConversationReminderStatusWS(conversationId, reminder.id, 'deleted');
      confirmDeleteReminder = null;
    } catch (err) {
      error = err?.message || 'Không thể xoá nhắc hẹn.';
    } finally {
      busyId = null;
    }
  }

  function openNoteModal() {
    noteContent = '';
    notePinned = false;
    showNoteModal = true;
  }

  async function createNote() {
    if (!canCreateNote) return;

    try {
      saving = true;
      error = '';
      await createConversationBoardItemWS(conversationId, {
        itemType: 'note',
        content: noteContent,
        pinned: notePinned
      });
      showNoteModal = false;
    } catch (err) {
      error = err?.message || 'Không thể tạo ghi chú.';
    } finally {
      saving = false;
    }
  }

  function openPollModal() {
    pollQuestion = '';
    pollOptions = ['', ''];
    showPollModal = true;
  }

  function updatePollOption(index, value) {
    pollOptions = pollOptions.map((option, optionIndex) => (optionIndex === index ? value : option));
  }

  function addPollOption() {
    if (pollOptions.length >= 10) return;
    pollOptions = [...pollOptions, ''];
  }

  function removePollOption(index) {
    if (pollOptions.length <= 2) return;
    pollOptions = pollOptions.filter((_, optionIndex) => optionIndex !== index);
  }

  async function createPoll() {
    if (!canCreatePoll) return;

    try {
      saving = true;
      error = '';
      await createConversationBoardItemWS(conversationId, {
        itemType: 'poll',
        content: pollQuestion,
        pollOptions: normalizedPollOptions
      });
      showPollModal = false;
    } catch (err) {
      error = err?.message || 'Không thể tạo bình chọn.';
    } finally {
      saving = false;
    }
  }

  function requestDeleteBoardItem(item) {
    if (!item?.id || busyId) return;

    confirmDeleteItem = item;
  }

  async function confirmDeleteBoardItem() {
    const item = confirmDeleteItem;
    if (!item?.id || busyId) return;

    try {
      busyId = item.id;
      error = '';
      await deleteConversationBoardItemWS(conversationId, item.id);
      confirmDeleteItem = null;
    } catch (err) {
      error = err?.message || 'Không thể xoá mục bảng tin.';
    } finally {
      busyId = null;
    }
  }

  async function votePoll(item, optionIndex) {
    if (!item?.id || busyId) return;

    try {
      busyId = `${item.id}-${optionIndex}`;
      error = '';
      await voteConversationPollWS(conversationId, item.id, optionIndex);
    } catch (err) {
      error = err?.message || 'Không thể gửi bình chọn.';
    } finally {
      busyId = null;
    }
  }
</script>

<div class="px-4 pb-4">
  {#if view === 'menu'}
    <div class="overflow-hidden rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        class="flex w-full items-center gap-3 px-3 py-3 text-left text-[13px] font-medium text-slate-800 transition hover:bg-slate-50"
        onclick={() => (view = 'reminders')}>
        <AlarmClock size={17} class="shrink-0 text-slate-500" />
        <span class="min-w-0 flex-1">Danh sách nhắc hẹn</span>
        {#if reminders.length}
          <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{reminders.length}</span>
        {/if}
      </button>

      <button
        type="button"
        class="flex w-full items-center gap-3 border-t border-slate-100 px-3 py-3 text-left text-[13px] font-medium text-slate-800 transition hover:bg-slate-50"
        onclick={() => (view = 'board')}>
        <ClipboardList size={17} class="shrink-0 text-slate-500" />
        <span class="min-w-0 flex-1">Ghi chú, ghim, bình chọn</span>
        {#if boardItems.length}
          <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{boardItems.length}</span>
        {/if}
      </button>
    </div>
  {:else}
    <div class="mb-3 flex items-center gap-2">
      <button
        type="button"
        class="grid h-8 w-8 shrink-0 place-items-center rounded-md text-slate-600 transition hover:bg-slate-100"
        aria-label="Quay lại bảng tin"
        onclick={() => (view = 'menu')}>
        <ArrowLeft size={17} />
      </button>

      <h3 class="min-w-0 flex-1 truncate text-[13px] font-semibold text-slate-950">
        {view === 'reminders' ? 'Danh sách nhắc hẹn' : 'Tiện ích'}
      </h3>

      <button
        type="button"
        class="grid h-8 w-8 shrink-0 place-items-center rounded-md text-slate-700 transition hover:bg-slate-100"
        aria-label="Tạo mới"
        onclick={() => (view === 'reminders' ? openReminderModal() : openNoteModal())}>
        <Plus size={18} />
      </button>
    </div>

    {#if error}
      <p class="mb-3 rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-[11px] text-red-600">{error}</p>
    {/if}

    {#if loading}
      <div class="flex items-center justify-center gap-2 rounded-lg bg-slate-50 px-3 py-8 text-[12px] text-slate-500">
        <LoaderCircle size={15} class="animate-spin" />
        Đang tải...
      </div>
    {:else if view === 'reminders'}
      {#if reminders.length}
        <div class="space-y-2">
          {#each reminders as reminder (reminder.id)}
            <article class="rounded-lg border border-slate-200 bg-white px-3 py-3">
              <div class="flex min-w-0 gap-3">
                <AlarmClock size={16} class="mt-0.5 shrink-0 text-blue-600" />
                <div class="min-w-0 flex-1">
                  <p class="line-clamp-2 text-[13px] font-medium leading-5 text-slate-900">{reminder.content}</p>
                  <p class="mt-1 text-[11px] text-slate-500">
                    {formatDateTime(reminder.remindAt)}
                    {#if reminder.repeatRule !== 'none'}
                      · {repeatOptions.find((item) => item.value === reminder.repeatRule)?.label}{/if}
                  </p>
                  <p class="mt-1 text-[11px] text-slate-400">{reminder.creatorName}</p>
                </div>
                <div class="flex shrink-0 gap-1">
                  <button
                    type="button"
                    class="grid h-7 w-7 place-items-center rounded-md text-emerald-600 transition hover:bg-emerald-50 disabled:opacity-50"
                    title="Hoàn tất"
                    disabled={!!busyId}
                    onclick={() => updateReminderStatus(reminder, 'done')}>
                    {#if busyId === reminder.id}
                      <LoaderCircle size={13} class="animate-spin" />
                    {:else}
                      <Check size={14} />
                    {/if}
                  </button>
                  <button
                    type="button"
                    class="grid h-7 w-7 place-items-center rounded-md text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                    title="Xoá"
                    disabled={!!busyId}
                    onclick={() => requestDeleteReminder(reminder)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </article>
          {/each}
        </div>
      {:else}
        <div class="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
          <AlarmClock size={28} class="mx-auto text-slate-300" />
          <p class="mt-3 text-[13px] font-medium text-slate-700">Chưa có nhắc hẹn</p>
          <p class="mt-1 text-[11px] text-slate-500">Tạo nhắc hẹn để cả nhóm không bỏ lỡ việc quan trọng.</p>
          <button
            type="button"
            class="mt-4 rounded-md bg-blue-600 px-4 py-2 text-[12px] font-medium text-white hover:bg-blue-700"
            onclick={openReminderModal}>
            Tạo nhắc hẹn
          </button>
        </div>
      {/if}
    {:else}
      <div class="mb-3 flex gap-2 overflow-x-auto border-b border-slate-200">
        {#each boardTabs as tab}
          <button
            type="button"
            class="shrink-0 border-b-2 px-2 pb-2 text-[12px] font-medium transition {boardTab === tab.id
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-slate-500 hover:text-slate-800'}"
            onclick={() => (boardTab = tab.id)}>
            {tab.label}
          </button>
        {/each}
      </div>

      {#if filteredBoardItems.length}
        <div class="space-y-2">
          {#each filteredBoardItems as item (item.id)}
            <article class="rounded-lg border border-slate-200 bg-white px-3 py-3">
              <div class="flex items-start gap-3">
                <div
                  class="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
                  {String(item.creatorName || '?')
                    .trim()
                    .charAt(0)
                    .toUpperCase() || '?'}
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex min-w-0 items-center gap-2">
                    <p class="truncate text-[12px] font-semibold text-slate-900">{item.creatorName}</p>
                    <span class="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-600">
                      {item.itemType === 'poll' ? 'Bình chọn' : item.pinned ? 'Tin ghim' : 'Ghi chú'}
                    </span>
                  </div>
                  <p class="mt-2 whitespace-pre-wrap text-[13px] leading-5 text-slate-900">{item.content}</p>

                  {#if item.itemType === 'poll'}
                    <div class="mt-3 space-y-2">
                      {#each item.pollOptions as option, index}
                        <button
                          type="button"
                          class="flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-[12px] transition {item.myVote ===
                          index
                            ? 'border-blue-200 bg-blue-50 text-blue-700'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}"
                          disabled={!!busyId}
                          onclick={() => votePoll(item, index)}>
                          {#if busyId === `${item.id}-${index}`}
                            <LoaderCircle size={13} class="animate-spin" />
                          {:else}
                            <Vote size={13} />
                          {/if}
                          <span class="min-w-0 flex-1 truncate">{option}</span>
                          <span class="text-[11px] text-slate-500">{item.voteCounts?.[index] || 0}</span>
                        </button>
                      {/each}
                    </div>
                  {/if}

                  <div class="mt-3 flex items-center justify-between gap-3 text-[11px] text-slate-500">
                    <span>{formatShortDate(item.createdAt)}</span>
                    <button
                      type="button"
                      class="text-red-600 hover:underline disabled:opacity-50"
                      disabled={!!busyId}
                      onclick={() => requestDeleteBoardItem(item)}>
                      Xoá
                    </button>
                  </div>
                </div>
              </div>
            </article>
          {/each}
        </div>
      {:else}
        <div class="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center">
          <ClipboardList size={28} class="mx-auto text-slate-300" />
          <p class="mt-3 text-[13px] font-medium text-slate-700">Chưa có nội dung bảng tin</p>
          <p class="mt-1 text-[11px] text-slate-500">Ghi chú và bình chọn sẽ nằm gọn ở đây.</p>
        </div>
      {/if}

      <div class="mt-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          class="rounded-md bg-blue-50 px-3 py-2 text-[12px] font-medium text-blue-700 hover:bg-blue-100"
          onclick={openNoteModal}>
          Tạo ghi chú
        </button>
        <button
          type="button"
          class="rounded-md bg-blue-50 px-3 py-2 text-[12px] font-medium text-blue-700 hover:bg-blue-100"
          onclick={openPollModal}>
          Tạo bình chọn
        </button>
      </div>
    {/if}
  {/if}
</div>

{#if showReminderModal}
  <div class="fixed inset-0 z-[70] grid place-items-center bg-slate-950/45 px-4 backdrop-blur-sm">
    <div class="w-full max-w-md rounded-xl bg-white shadow-2xl">
      <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <h3 class="text-base font-semibold text-slate-950">Tạo nhắc hẹn</h3>
        <button
          type="button"
          class="grid h-8 w-8 place-items-center rounded-md text-slate-600 hover:bg-slate-100"
          onclick={() => (showReminderModal = false)}>
          <X size={18} />
        </button>
      </div>
      <div class="space-y-4 px-5 py-4">
        <label class="block text-[13px] font-medium text-slate-800">
          Nội dung
          <textarea
            bind:value={reminderContent}
            rows="4"
            class="mt-2 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            placeholder="Nhập nội dung nhắc hẹn">
          </textarea>
        </label>

        <div>
          <p class="text-[13px] font-medium text-slate-800">Chọn thời gian</p>
          <div class="mt-2 flex flex-wrap gap-2">
            <button
              type="button"
              class="time-chip"
              onclick={() => (reminderAt = toInputDateTime(addMinutes(new Date(), 15)))}>
              15 phút nữa
            </button>
            <button
              type="button"
              class="time-chip"
              onclick={() => (reminderAt = toInputDateTime(addMinutes(new Date(), 30)))}>
              30 phút nữa
            </button>
            <button type="button" class="time-chip" onclick={() => (reminderAt = toInputDateTime(tomorrowAt(9, 0)))}>
              9:00 ngày mai
            </button>
          </div>
        </div>

        <label class="block text-[13px] font-medium text-slate-800">
          Ngày nhắc hẹn
          <input
            bind:value={reminderAt}
            type="datetime-local"
            class="mt-2 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
        </label>

        <label class="block text-[13px] font-medium text-slate-800">
          Lặp lại
          <select
            bind:value={reminderRepeat}
            class="mt-2 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
            {#each repeatOptions as option}
              <option value={option.value}>{option.label}</option>
            {/each}
          </select>
        </label>
      </div>
      <div class="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
        <button
          type="button"
          class="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
          onclick={() => (showReminderModal = false)}>
          Hủy
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={!canCreateReminder}
          onclick={createReminder}>
          {#if saving}<LoaderCircle size={14} class="animate-spin" />{/if}
          Tạo nhắc hẹn
        </button>
      </div>
    </div>
  </div>
{/if}

<ConversationConfirmDialog
  open={!!confirmDeleteItem}
  title="Xoá mục bảng tin?"
  description="Mục này sẽ bị xoá khỏi bảng tin của nhóm và không thể khôi phục."
  confirmLabel="Xoá"
  tone="danger"
  loading={!!busyId}
  onCancel={() => (confirmDeleteItem = null)}
  onConfirm={confirmDeleteBoardItem} />

<ConversationConfirmDialog
  open={!!confirmDeleteReminder}
  title="Xoá nhắc hẹn?"
  description="Nhắc hẹn này sẽ bị xoá khỏi danh sách và không thể khôi phục."
  confirmLabel="Xoá"
  tone="danger"
  loading={!!busyId}
  onCancel={() => (confirmDeleteReminder = null)}
  onConfirm={confirmDeleteReminderItem} />

{#if showNoteModal}
  <div class="fixed inset-0 z-[70] grid place-items-center bg-slate-950/45 px-4 backdrop-blur-sm">
    <div class="w-full max-w-md rounded-xl bg-white shadow-2xl">
      <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <h3 class="text-base font-semibold text-slate-950">Tạo ghi chú</h3>
        <button
          type="button"
          class="grid h-8 w-8 place-items-center rounded-md text-slate-600 hover:bg-slate-100"
          onclick={() => (showNoteModal = false)}>
          <X size={18} />
        </button>
      </div>
      <div class="space-y-4 px-5 py-4">
        <label class="block text-[13px] font-medium text-slate-800">
          Nội dung
          <textarea
            bind:value={noteContent}
            rows="6"
            class="mt-2 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            placeholder="Nhập nội dung hoặc dán link">
          </textarea>
        </label>
        <label class="inline-flex items-center gap-2 text-[13px] text-slate-700">
          <input bind:checked={notePinned} type="checkbox" class="h-4 w-4 rounded border-slate-300" />
          Ghim lên đầu bảng tin
        </label>
      </div>
      <div class="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
        <button
          type="button"
          class="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
          onclick={() => (showNoteModal = false)}>
          Hủy
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={!canCreateNote}
          onclick={createNote}>
          {#if saving}<LoaderCircle size={14} class="animate-spin" />{/if}
          Tạo ghi chú
        </button>
      </div>
    </div>
  </div>
{/if}

{#if showPollModal}
  <div class="fixed inset-0 z-[70] grid place-items-center bg-slate-950/45 px-4 backdrop-blur-sm">
    <div class="w-full max-w-md rounded-xl bg-white shadow-2xl">
      <div class="flex items-center justify-between border-b border-slate-200 px-5 py-4">
        <h3 class="text-base font-semibold text-slate-950">Tạo bình chọn</h3>
        <button
          type="button"
          class="grid h-8 w-8 place-items-center rounded-md text-slate-600 hover:bg-slate-100"
          onclick={() => (showPollModal = false)}>
          <X size={18} />
        </button>
      </div>
      <div class="space-y-4 px-5 py-4">
        <label class="block text-[13px] font-medium text-slate-800">
          Chủ đề bình chọn
          <textarea
            bind:value={pollQuestion}
            rows="3"
            maxlength="200"
            class="mt-2 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            placeholder="Đặt câu hỏi bình chọn">
          </textarea>
        </label>

        <div>
          <p class="text-[13px] font-medium text-slate-800">Các lựa chọn</p>
          <div class="mt-2 space-y-2">
            {#each pollOptions as option, index}
              <div class="flex gap-2">
                <input
                  value={option}
                  oninput={(event) => updatePollOption(index, event.currentTarget.value)}
                  class="h-10 min-w-0 flex-1 rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  placeholder={`Lựa chọn ${index + 1}`} />
                {#if pollOptions.length > 2}
                  <button
                    type="button"
                    class="grid h-10 w-10 place-items-center rounded-lg text-red-600 hover:bg-red-50"
                    onclick={() => removePollOption(index)}>
                    <X size={16} />
                  </button>
                {/if}
              </div>
            {/each}
          </div>
          <button
            type="button"
            class="mt-3 text-[13px] font-medium text-blue-600 hover:underline"
            onclick={addPollOption}>
            Thêm lựa chọn
          </button>
        </div>
      </div>
      <div class="flex justify-end gap-2 border-t border-slate-200 px-5 py-4">
        <button
          type="button"
          class="rounded-lg bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200"
          onclick={() => (showPollModal = false)}>
          Hủy
        </button>
        <button
          type="button"
          class="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={!canCreatePoll}
          onclick={createPoll}>
          {#if saving}<LoaderCircle size={14} class="animate-spin" />{/if}
          Tạo bình chọn
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .time-chip {
    border-radius: 9999px;
    background: #f1f5f9;
    padding: 0.45rem 0.75rem;
    color: #334155;
    font-size: 0.8125rem;
    font-weight: 500;
  }

  .time-chip:hover {
    background: #e2e8f0;
  }
</style>
